import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NiceModal from '@ebay/nice-modal-react';
import { listings } from '@prisma/client';
import marketAnalysisService, {
  MarketAnalysis as MarketAnalysisData,
  MarketAnalysisArea,
  MarketAnalysisListingStatus,
  MarketAnalysisStatKey,
  VerdictTone,
} from '../../api/services/marketAnalysis';
import BuyerOfferModal from '../OfferModal/BuyerOfferModal';
import useIsMobile from '../../hooks/useIsMobile';
import useIsWaitlisted from '../../hooks/useIsWaitlisted';
import { smsHref } from '../../utils/contactHomepoint';
import { cleanMlsStatus } from '../../utils/mlsStatus';

interface Props {
  listing: listings;
  readOnly?: boolean;
  hasPendingOffer?: boolean;
}

/**
 * The heading above the stat tiles. A radius already reads as a phrase
 * ("within 3 miles"), so it is capitalised as-is rather than being bolted
 * onto "market", which would give "within 3 miles market".
 */
const areaHeading = (area: MarketAnalysisArea): string => {
  if (area.kind === 'radius') {
    return area.name.charAt(0).toUpperCase() + area.name.slice(1);
  }
  return `${area.name} market`;
};

/** Copy for the empty state, keyed by why there is no defensible number. */
const UNAVAILABLE_COPY: Record<string, string> = {
  NO_COMPS: 'Not enough recent sales nearby to value this home',
  COMPS_DISAGREE:
    'Recent sales nearby disagree too much to stand behind a number',
};

/** Status-card copy is always neutral -- it states a fact, not a verdict. */
const STATUS_LABELS: Record<MarketAnalysisListingStatus, string> = {
  ACTIVE: 'Active',
  PENDING: 'Under contract',
  SOLD: 'Sold',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn',
  UNKNOWN: 'Status unknown',
};

/**
 * The amber "caution" pair. Taken as literals because the theme's `highlight`
 * entry is declared as a bare string in augmentations.d.ts, so `.main` is not
 * reachable through the typed palette. WARN_TEXT is the darkened twin used for
 * type on a light tint, where WARN itself does not carry enough contrast.
 */
const WARN = '#f39c12';
const WARN_TEXT = '#b9720a';

const STAT_ICONS: Record<
  MarketAnalysisStatKey,
  typeof CalendarTodayOutlinedIcon
> = {
  daysOnMarket: CalendarTodayOutlinedIcon,
  listToSaleRatio: SellOutlinedIcon,
  priceCuts: TrendingDownOutlinedIcon,
  monthsOfSupply: Inventory2OutlinedIcon,
};

/** "$425,000" -> "$425K". Mirrors the API's own band formatting. */
const compactPrice = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value)}`;
};

const fullPrice = (value: number): string =>
  `$${Math.round(value).toLocaleString()}`;

/** Flat tinted tile that fronts each card — alpha fill, alpha border, no gradient. */
export const IconTile: React.FC<{
  color: string;
  children: React.ReactNode;
}> = ({ color, children }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      flexShrink: 0,
      borderRadius: 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: alpha(color, 0.08),
      border: `1px solid ${alpha(color, 0.2)}`,
      color,
    }}
  >
    {children}
  </Box>
);

export const Overline: React.FC<{
  color?: string;
  children: React.ReactNode;
}> = ({ color, children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '1.2px',
      textTransform: 'uppercase',
      lineHeight: 1.4,
      color: color || 'text.secondary',
    }}
  >
    {children}
  </Typography>
);

export const SectionCard: React.FC<{
  testId?: string;
  children: React.ReactNode;
  sx?: object;
}> = ({ testId, children, sx }) => (
  <Card
    elevation={0}
    data-testid={testId}
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 2,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      backgroundColor: 'background.paper',
      ...sx,
    }}
  >
    {children}
  </Card>
);

/**
 * "Market Analysis" — the buyer's read on whether a listing is priced right,
 * built from real sold comps rather than a ZIP median. Verdict first, then the
 * value band with the list price marked on it, the four area stats, the comps
 * the estimate rests on, and what it all means for an offer.
 *
 * Buyer-only: the API refuses a seller asking about their own listing, and the
 * detail page only mounts this in the buyer view.
 */
const MarketAnalysis: React.FC<Props> = ({
  listing,
  readOnly = false,
  hasPendingOffer = false,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isWaitlisted = useIsWaitlisted();

  const [data, setData] = useState<MarketAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const listingId = listing?.id;

  useEffect(() => {
    let cancelled = false;
    if (!listingId) return undefined;

    setLoading(true);
    setFailed(false);

    marketAnalysisService
      .find(listingId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [listingId]);

  const toneColor = useCallback(
    (tone: VerdictTone): string => {
      const colors: Record<VerdictTone, string> = {
        primary: theme.palette.primary.main,
        success: theme.palette.success.main,
        warn: WARN,
        danger: theme.palette.error.main,
      };
      return colors[tone] || theme.palette.primary.main;
    },
    [theme]
  );

  const toneTextColor = useCallback(
    (tone: VerdictTone): string =>
      tone === 'warn' ? WARN_TEXT : toneColor(tone),
    [toneColor]
  );

  const band = data?.valueBand ?? null;
  const listPrice = data?.property.listPrice ?? 0;

  /**
   * The band track's axis: wide enough to hold both the estimate and the list
   * price, with headroom so a marker at either extreme still has a label.
   */
  const scale = useMemo(() => {
    if (!band) return null;
    const min = Math.min(band.low, listPrice);
    const max = Math.max(band.high, listPrice);
    const pad = Math.max((max - min) * 0.2, band.mid * 0.02);
    return { min: min - pad, max: max + pad };
  }, [band, listPrice]);

  const positionPct = useCallback(
    (value: number): number => {
      if (!scale || scale.max === scale.min) return 0;
      const pct = ((value - scale.min) / (scale.max - scale.min)) * 100;
      return Math.min(100, Math.max(0, pct));
    },
    [scale]
  );

  const handleDraftOffer = () => {
    NiceModal.show(BuyerOfferModal, {
      listing,
      initialPrice: data?.offerGuidance?.suggested,
    });
  };

  if (!listingId || failed) return null;

  if (loading) {
    return (
      <Stack spacing={2} data-testid="market-analysis-loading">
        <SectionCard>
          <Skeleton variant="text" width={140} height={18} />
          <Skeleton variant="text" width="70%" height={32} />
          <Skeleton
            variant="rectangular"
            height={8}
            sx={{ mt: 2, borderRadius: 1 }}
          />
        </SectionCard>
        <Grid2 container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid2 xs={6} key={i}>
              <SectionCard>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="text" width="90%" />
              </SectionCard>
            </Grid2>
          ))}
        </Grid2>
        <SectionCard>
          <Skeleton variant="text" width={180} height={24} />
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="text" height={28} />
          ))}
        </SectionCard>
      </Stack>
    );
  }

  if (!data) return null;

  const { property, verdict, stats, comps, offerGuidance } = data;

  // Days on market is already the header's job (and shows up again in the
  // verdict sentence below), so drop that stat here. When only one stat
  // survives the filter, a single-tile grid row reads as a mistake, so it
  // renders inline in the verdict card instead.
  const visibleStats = stats.filter((stat) => stat.key !== 'daysOnMarket');
  const gridStats = visibleStats.length > 1 ? visibleStats : [];
  const inlineStat = visibleStats.length === 1 ? visibleStats[0] : null;

  const inlineStatRow = inlineStat && (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      data-testid="market-analysis-inline-stat"
      data-stat-key={inlineStat.key}
      sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}
    >
      <IconTile color={theme.palette.primary.main}>
        {React.createElement(STAT_ICONS[inlineStat.key], {
          fontSize: 'small',
        })}
      </IconTile>
      <Stack
        direction="row"
        spacing={1}
        alignItems="baseline"
        flexWrap="wrap"
        sx={{ minWidth: 0 }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={800}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {inlineStat.value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {inlineStat.label} · {inlineStat.detail}
        </Typography>
      </Stack>
    </Stack>
  );

  // The tiles describe one geography -- the homes within a few miles of this
  // one, the neighborhood it sits in, or the ZIP -- and the label above them
  // says which, so "6.5 mo" is never read as a figure for somewhere else. A
  // boundary-derived stat block also carries its boundary's credit line
  // underneath, which is a licensing condition of those polygons; a radius is
  // measured off our own data and owes nobody a credit.
  const statsGrid = gridStats.length > 0 && (
    <Box data-testid="market-analysis-stats-section">
      {data.area && (
        <Box sx={{ mb: 1 }}>
          <Overline>
            <span
              data-testid="market-analysis-area-label"
              data-area-kind={data.area.kind}
            >
              {areaHeading(data.area)}
            </span>
          </Overline>
        </Box>
      )}
      <Grid2 container spacing={2} data-testid="market-analysis-stats">
        {gridStats.map((stat) => {
          const StatIcon = STAT_ICONS[stat.key];
          return (
            <Grid2 xs={6} key={stat.key}>
              <SectionCard>
                <Stack
                  spacing={1}
                  data-testid="market-analysis-stat"
                  data-stat-key={stat.key}
                >
                  <IconTile color={theme.palette.primary.main}>
                    <StatIcon fontSize="small" />
                  </IconTile>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {stat.value}
                  </Typography>
                  <Overline>{stat.label}</Overline>
                  <Typography variant="caption" color="text.secondary">
                    {stat.detail}
                  </Typography>
                </Stack>
              </SectionCard>
            </Grid2>
          );
        })}
      </Grid2>
      {data.area?.attribution && (
        <Typography
          variant="caption"
          data-testid="market-analysis-area-attribution"
          sx={{
            display: 'block',
            mt: 1,
            fontSize: 11,
            color: 'text.secondary',
          }}
        >
          {data.area.attribution}
        </Typography>
      )}
    </Box>
  );

  const sourcesLine = (
    <Typography
      variant="caption"
      data-testid="market-analysis-sources"
      sx={{
        display: 'block',
        textAlign: 'center',
        fontSize: 11,
        color: 'text.secondary',
      }}
    >
      {data.sources}
    </Typography>
  );

  // Non-ACTIVE listings carry a factual note (when this sold, that another
  // buyer is under contract) ahead of anything else on the card -- it sits
  // above the verdict whether or not a verdict/empty-state exists below it.
  const statusCard = data.statusNote && (
    <SectionCard testId="market-analysis-status">
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <IconTile color={theme.palette.text.secondary}>
          <InfoOutlinedIcon fontSize="small" />
        </IconTile>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Overline>Status</Overline>
            <Chip
              label={
                cleanMlsStatus(data.mlsStatus) ??
                STATUS_LABELS[data.listingStatus] ??
                data.listingStatus
              }
              size="small"
              data-testid="market-analysis-status-chip"
              sx={{
                // A full MLS status runs long and must not be ellipsized.
                height: 'auto',
                minHeight: 20,
                maxWidth: '100%',
                '& .MuiChip-label': { whiteSpace: 'normal', py: '2px' },
                fontSize: 11,
                fontWeight: 700,
                color: theme.palette.text.secondary,
                backgroundColor: alpha(theme.palette.text.secondary, 0.08),
                border: `1px solid ${alpha(theme.palette.text.secondary, 0.2)}`,
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {data.statusNote}
          </Typography>
        </Box>
      </Stack>
    </SectionCard>
  );

  if (!data.available || !verdict || !band) {
    return (
      <Stack spacing={2} data-testid="market-analysis">
        {statusCard}
        <SectionCard testId="market-analysis-empty">
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <IconTile color={theme.palette.primary.main}>
              <SearchOutlinedIcon fontSize="small" />
            </IconTile>
            <Box>
              <Overline color={theme.palette.primary.main}>
                Reva&apos;s read
              </Overline>
              <Typography variant="body1" fontWeight={700} sx={{ mt: 0.25 }}>
                {UNAVAILABLE_COPY[data.unavailableReason ?? 'NO_COMPS'] ??
                  UNAVAILABLE_COPY.NO_COMPS}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Nothing comparable has sold close enough to stand behind a
                number here, and Reva will not guess. Text Caroline and she will
                talk through what is available and what it means for an offer.
              </Typography>
              {!readOnly && (
                <Button
                  variant="contained"
                  size="small"
                  component="a"
                  startIcon={<SmsOutlinedIcon />}
                  href={smsHref(
                    `Hi Caroline — I'm looking at ${property.address} and ` +
                      "Reva couldn't value it from recent sales. " +
                      'Can we talk through pricing?'
                  )}
                  data-testid="market-analysis-text-caroline"
                  sx={{ mt: 1.5 }}
                >
                  Text Caroline
                </Button>
              )}
            </Box>
          </Stack>
          {inlineStatRow}
        </SectionCard>
        {statsGrid}
        {sourcesLine}
      </Stack>
    );
  }

  const verdictColor = toneColor(verdict.tone);
  const verdictTextColor = toneTextColor(verdict.tone);

  return (
    <Stack spacing={2} data-testid="market-analysis">
      {statusCard}
      <SectionCard testId="market-analysis-verdict">
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <IconTile color={verdictColor}>
            <SearchOutlinedIcon fontSize="small" />
          </IconTile>
          <Box sx={{ minWidth: 0 }}>
            <Overline color={verdictTextColor}>Reva&apos;s read</Overline>
            <Typography
              data-testid="market-analysis-verdict-title"
              data-verdict-key={verdict.key}
              sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.3, mt: 0.25 }}
            >
              {verdict.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="market-analysis-verdict-sentence"
              sx={{ mt: 0.75, lineHeight: 1.6 }}
            >
              {verdict.sentence}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ mt: 2.5 }}>
          <Overline>Estimated value</Overline>
          <Stack
            direction="row"
            spacing={1}
            alignItems="baseline"
            sx={{ mt: 0.25 }}
          >
            <Typography
              data-testid="market-analysis-value-range"
              sx={{
                fontSize: 20,
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {compactPrice(band.low)}–{compactPrice(band.high)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="market-analysis-value-mid"
            >
              midpoint {compactPrice(band.mid)}
            </Typography>
          </Stack>
        </Box>

        {/* Value band: estimate segment, midpoint tick, and the list price marked
            where it actually falls against the comps. */}
        <Box
          sx={{ mt: 4, mb: 3.5, position: 'relative' }}
          data-testid="market-analysis-band"
        >
          <Box
            sx={{
              position: 'relative',
              height: 8,
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.text.primary, 0.06),
            }}
          >
            <Box
              data-testid="market-analysis-band-range"
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${positionPct(band.low)}%`,
                width: `${positionPct(band.high) - positionPct(band.low)}%`,
                borderRadius: 4,
                backgroundColor: alpha(theme.palette.primary.main, 0.18),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            />
            <Box
              data-testid="market-analysis-band-mid"
              sx={{
                position: 'absolute',
                top: -3,
                height: 14,
                width: 2,
                left: `${positionPct(band.mid)}%`,
                backgroundColor: theme.palette.primary.dark,
                borderRadius: 1,
              }}
            />
            <Box
              data-testid="market-analysis-list-marker"
              sx={{
                position: 'absolute',
                top: -6,
                height: 20,
                width: 3,
                left: `${positionPct(listPrice)}%`,
                transform: 'translateX(-1px)',
                backgroundColor: verdictColor,
                borderRadius: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 20,
                left: `${positionPct(listPrice)}%`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              <Box
                data-testid="market-analysis-list-chip"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: 11,
                  fontWeight: 700,
                  color: verdictTextColor,
                  backgroundColor: alpha(verdictColor, 0.08),
                  border: `1px solid ${alpha(verdictColor, 0.2)}`,
                }}
              >
                List {compactPrice(listPrice)}
              </Box>
            </Box>
          </Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ position: 'absolute', left: 0, right: 0, top: 14 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {compactPrice(scale?.min ?? band.low)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {compactPrice(scale?.max ?? band.high)}
            </Typography>
          </Stack>
        </Box>

        {inlineStatRow}
      </SectionCard>

      {statsGrid}

      {comps.length > 0 && (
        <SectionCard testId="market-analysis-comps">
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <IconTile color={theme.palette.secondary.main}>
              <HomeWorkOutlinedIcon fontSize="small" />
            </IconTile>
            <Typography variant="subtitle1" fontWeight={700}>
              Comparable sold homes
            </Typography>
          </Stack>
          <Stack
            divider={
              <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }} />
            }
          >
            {comps.map((comp) => (
              <Stack
                key={comp.id}
                data-testid="market-analysis-comp"
                direction={isMobile ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems={isMobile ? 'flex-start' : 'center'}
                spacing={isMobile ? 0.25 : 2}
                sx={{ py: 1.25 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {comp.address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      comp.soldDaysAgo !== null
                        ? `${comp.soldDaysAgo} days ago`
                        : null,
                      `${comp.beds} bd/${comp.baths} ba`,
                      `${comp.area.toLocaleString()} sqft`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                >
                  {fullPrice(comp.soldPrice)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}

      {offerGuidance && (
        <SectionCard
          testId="market-analysis-offer"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
          }}
        >
          <Overline color={theme.palette.primary.dark}>
            What this means for your offer
          </Overline>
          <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.6 }}>
            {offerGuidance.text}
          </Typography>
          {!isWaitlisted && !readOnly && !hasPendingOffer && (
            <Button
              variant="contained"
              fullWidth
              data-testid="market-analysis-offer-cta"
              onClick={handleDraftOffer}
              sx={{ mt: 2 }}
            >
              Draft an offer
            </Button>
          )}
        </SectionCard>
      )}

      {sourcesLine}
    </Stack>
  );
};

export default MarketAnalysis;
